import { Analytics } from '@easy-expense/analytics-client';
import { useIsAdminRole, useWorkspaceSettings } from '@easy-expense/data-firestore-client';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { getCombinedExpenseStatus } from '@easy-expense/utils-client';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { ExpenseTable } from '../Expense/MaterialExpenseTable.component';
import { CloseButton } from '../Shared/CloseButton.component';

export const ReviewTransactions: React.FC = () => {
  const navigate = useNavigate();
  const isAdmin = useIsAdminRole();
  const allExpenses = Data.expenses.use();
  const { workspaceSettings } = useWorkspaceSettings();

  const requiresApproval = workspaceSettings?.requiresApproval ?? false;
  const showsReimbursements = workspaceSettings?.showReimbursementsToggle ?? false;

  // Mirrors ApprovalBanner in TransactionListHeader.component.tsx — only 'pending' and
  // 'needsReimbursement' expenses need review, gated on the workflows the workspace actually
  // runs so a stale approvalStatus/isReimbursable from a disabled toggle doesn't leak in.
  const reviewExpenseKeys = React.useMemo(
    () =>
      allExpenses
        .filter((expense) => {
          const status = getCombinedExpenseStatus(expense);
          if (status === 'pending') {
            return requiresApproval;
          }
          if (status === 'needsReimbursement') {
            return showsReimbursements;
          }
          return false;
        })
        .map((expense) => expense.key),
    [allExpenses, requiresApproval, showsReimbursements],
  );

  React.useEffect(() => {
    Analytics.track('approval_queue_viewed', {
      role: isAdmin ? 'admin' : 'manager',
      queueType: 'expenses',
      pendingCount: reviewExpenseKeys.length,
      platform: 'web',
    });
  }, [isAdmin, reviewExpenseKeys.length]);

  return (
    <Layout.Row px={24} style={{ height: '100vh', width: '100%' }}>
      <Spacer.Flex />
      <Layout.Column style={{ height: '100vh', minWidth: 1100 }}>
        <Layout.Row py align>
          <OpenSans.Primary weight="bold-700" size={32}>
            {getTranslation('Review Transactions')}
          </OpenSans.Primary>
        </Layout.Row>

        <ExpenseTable
          // Carrying a returnTo keeps the approvals queue out of the
          // transactions list's Resume Row, and returns the reviewer here rather
          // than to the transactions list once they are done with an expense.
          onRowClick={(expense) =>
            navigate(`/expense/${expense}`, { state: { returnTo: '/approvals' } })
          }
          expenseList={reviewExpenseKeys}
        />
      </Layout.Column>
      <Spacer.Flex />
      <CloseButton onClose={() => navigate('/transactions')} />
    </Layout.Row>
  );
};
